import React, { useState, useRef } from 'react';
import { X, Play, Pause, Scissors, RotateCcw, Eye } from 'lucide-react';

const fmt = (v) => {
  v = Math.max(0, Math.floor(v || 0));
  const h = Math.floor(v / 3600), m = Math.floor((v % 3600) / 60), s = v % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
};

export const fmtTrim = (s, e) => {
  if (s > 0 && e > 0) return `(${fmt(s)} → ${fmt(e)})`;
  if (s > 0) return `(commence à ${fmt(s)})`;
  if (e > 0) return `(s'arrête à ${fmt(e)})`;
  return '';
};

function TimeParts({ value, onChange, light, testid }) {
  const h = Math.floor((value || 0) / 3600), m = Math.floor(((value || 0) % 3600) / 60), s = Math.floor((value || 0) % 60);
  const cls = `w-12 rounded px-1 py-1.5 text-xs text-center focus:outline-none focus:border-[#E9641A] border ${light ? 'bg-white border-gray-200 text-gray-800' : 'bg-white/5 border-white/10 text-white'}`;
  const setP = (nh, nm, ns) => onChange(Math.max(0, nh * 3600 + nm * 60 + ns));
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      <input type="number" min="0" value={h} onChange={e => setP(parseInt(e.target.value) || 0, m, s)} className={cls} />
      <span className={light ? 'text-gray-400' : 'text-white/40'}>:</span>
      <input type="number" min="0" max="59" value={m} onChange={e => setP(h, Math.min(59, parseInt(e.target.value) || 0), s)} className={cls} />
      <span className={light ? 'text-gray-400' : 'text-white/40'}>:</span>
      <input type="number" min="0" max="59" value={s} onChange={e => setP(h, m, Math.min(59, parseInt(e.target.value) || 0))} className={cls} />
    </div>
  );
}

export function TrimEditor({ url, label, start = 0, end = null, onApply, onClose, light }) {
  const videoRef = useRef(null);
  const previewRef = useRef(false);
  const [dur, setDur] = useState(0);
  const [cur, setCur] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [s, setS] = useState(start || 0);
  const [e, setE] = useState(end || 0); // 0 = jusqu'à la fin
  const [error, setError] = useState('');

  const effEnd = e > 0 ? e : dur;
  const hasTrim = s > 0 || e > 0;

  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    previewRef.current = false;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  };

  const preview = () => {
    const v = videoRef.current; if (!v) return;
    previewRef.current = true;
    v.currentTime = s;
    v.play().catch(() => {});
  };

  const apply = () => {
    if (e > 0 && e <= s) { setError('La fin doit être après le début.'); return; }
    if (dur > 0 && s >= dur) { setError('Le début dépasse la durée de la vidéo.'); return; }
    onApply(Math.floor(s), e > 0 ? Math.floor(e) : null);
  };

  const panelCls = light ? 'bg-white border-gray-200' : 'bg-[#1a0e2e] border-white/10';
  const txt = light ? 'text-gray-800' : 'text-white';
  const hint = light ? 'text-gray-400' : 'text-white/40';
  const btnCls = `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${light ? 'border-gray-200 text-gray-600 hover:border-[#E9641A] hover:text-[#E9641A]' : 'border-white/10 text-white/60 hover:border-[#E9641A] hover:text-[#E9641A]'}`;

  return (
    <div className="fixed inset-0 z-[999] bg-black/75 flex items-center justify-center p-4" onClick={onClose} data-testid="trim-editor-modal">
      <div className={`rounded-xl border w-full max-w-3xl p-5 ${panelCls}`} onClick={ev => ev.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`text-sm font-bold flex items-center gap-2 ${txt}`}><Scissors size={15} className="text-[#E9641A]" /> Découper — {label}</h3>
          <button onClick={onClose} className={hint} data-testid="trim-close-btn"><X size={18} /></button>
        </div>

        <div className="relative rounded-lg overflow-hidden bg-black mb-3">
          <video ref={videoRef} src={url} className="w-full max-h-[340px] object-contain" playsInline
            onLoadedMetadata={ev => setDur(ev.target.duration || 0)}
            onTimeUpdate={ev => {
              const t = ev.target.currentTime;
              setCur(t);
              if (previewRef.current && effEnd > 0 && t >= effEnd) { ev.target.pause(); previewRef.current = false; }
            }}
            onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
            data-testid="trim-video" />
        </div>

        {/* Scrub bar with kept zone */}
        <div className="mb-1">
          <div className="relative h-2.5 rounded-full overflow-hidden cursor-pointer" style={{ background: light ? '#fecaca' : 'rgba(220,38,38,0.35)' }}
            onClick={ev => {
              const rect = ev.currentTarget.getBoundingClientRect();
              const pct = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
              if (videoRef.current && dur > 0) { previewRef.current = false; videoRef.current.currentTime = pct * dur; }
            }} data-testid="trim-scrub-bar">
            {dur > 0 && (
              <div className="absolute inset-y-0 bg-[#E9641A]/80" style={{ left: `${(s / dur) * 100}%`, width: `${Math.max(0, ((effEnd - s) / dur) * 100)}%` }} />
            )}
            {dur > 0 && <div className="absolute inset-y-0 w-0.5 bg-white" style={{ left: `${(cur / dur) * 100}%` }} />}
          </div>
          <div className={`flex justify-between text-[10px] mt-1 ${hint}`}>
            <span data-testid="trim-current-time">{fmt(cur)}</span>
            <span>La zone <span className="text-[#E9641A] font-semibold">orange</span> sera conservée · Durée finale : <span className={`font-semibold ${txt}`}>{fmt(Math.max(0, effEnd - s))}</span> / {fmt(dur)}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <button onClick={togglePlay} className={btnCls} data-testid="trim-play-btn">{playing ? <Pause size={12} /> : <Play size={12} />} {playing ? 'Pause' : 'Lire'}</button>
          <button onClick={() => { if (videoRef.current) { previewRef.current = false; videoRef.current.currentTime = Math.max(0, cur - 1); } }} className={btnCls}>-1s</button>
          <button onClick={() => { if (videoRef.current) { previewRef.current = false; videoRef.current.currentTime = cur + 1; } }} className={btnCls}>+1s</button>
          <button onClick={preview} className={btnCls} data-testid="trim-preview-btn"><Eye size={12} /> Prévisualiser le résultat</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div className={`rounded-lg border p-3 ${light ? 'border-gray-100 bg-gray-50' : 'border-white/5 bg-white/[0.03]'}`}>
            <p className={`text-[10px] uppercase tracking-wider mb-2 ${hint}`}>Couper le début — la vidéo commence à</p>
            <div className="flex items-center gap-2">
              <TimeParts value={s} onChange={v => { setError(''); setS(v); }} light={light} testid="trim-start-input" />
              <button onClick={() => { setError(''); setS(Math.floor(cur)); }} className={btnCls} data-testid="trim-set-start-btn">Position actuelle</button>
            </div>
          </div>
          <div className={`rounded-lg border p-3 ${light ? 'border-gray-100 bg-gray-50' : 'border-white/5 bg-white/[0.03]'}`}>
            <p className={`text-[10px] uppercase tracking-wider mb-2 ${hint}`}>Couper la fin — la vidéo s'arrête à <span className="normal-case">(0:00 = fin d'origine)</span></p>
            <div className="flex items-center gap-2">
              <TimeParts value={e} onChange={v => { setError(''); setE(v); }} light={light} testid="trim-end-input" />
              <button onClick={() => { setError(''); setE(Math.floor(cur)); }} className={btnCls} data-testid="trim-set-end-btn">Position actuelle</button>
            </div>
          </div>
        </div>

        {error && <p className="text-xs text-red-500 mb-3" data-testid="trim-error">{error}</p>}

        <div className="flex items-center justify-between">
          <p className={`text-[10px] ${hint}`}>Découpe non destructive : la vidéo d'origine est conservée, vous pouvez la rétablir à tout moment.</p>
          <div className="flex items-center gap-2">
            {hasTrim && (
              <button onClick={() => onApply(0, null)} className={btnCls} data-testid="trim-reset-btn"><RotateCcw size={12} /> Rétablir l'original</button>
            )}
            <button onClick={apply} className="flex items-center gap-1.5 bg-[#E9641A] hover:bg-[#FE975E] text-white text-[11px] font-semibold px-4 py-2 rounded-lg transition-colors" data-testid="trim-apply-btn">
              <Scissors size={12} /> Valider la découpe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
